import client from "./client";

export type EntityType =
  | "leave_request"
  | "expense"
  | "job_requisition"
  | "document_change"
  | "compensation_change";

export type ApproverType =
  | "direct_manager"
  | "skip_level_manager"
  | "department_head"
  | "hr_admin"
  | "specific_user"
  | "role";

export type ConditionOperator =
  | "=="
  | "!="
  | ">"
  | ">="
  | "<"
  | "<="
  | "in"
  | "not_in";

export interface StepCondition {
  field: string;
  operator: ConditionOperator;
  value: unknown;
}

export interface WorkflowStep {
  id?: string;
  step_order: number;
  approver_type: ApproverType;
  approver_id?: string | null;
  approver_role?: string | null;
  condition?: StepCondition | null;
  auto_approve: boolean;
}

export interface Workflow {
  id: string;
  org_id: string;
  name: string;
  entity_type: EntityType;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  steps: WorkflowStep[];
}

export interface WorkflowCreateInput {
  name: string;
  entity_type: EntityType;
  is_active?: boolean;
  steps: WorkflowStep[];
}

export interface WorkflowUpdateInput {
  name?: string;
  entity_type?: EntityType;
  is_active?: boolean;
  steps?: WorkflowStep[];
}

export interface ResolvedStep {
  step_order: number;
  approver_type: ApproverType;
  approver_role: string | null;
  approver_user_ids: string[];
  auto_approved: boolean;
  skipped: boolean;
  reason: string | null;
}

export interface EvaluationResult {
  workflow_id: string | null;
  entity_type: EntityType;
  matched: boolean;
  steps: ResolvedStep[];
}

export async function listWorkflows(params?: {
  entity_type?: EntityType;
  include_inactive?: boolean;
}): Promise<Workflow[]> {
  const res = await client.get("/workflows", { params });
  return res.data;
}

export async function createWorkflow(
  input: WorkflowCreateInput,
): Promise<Workflow> {
  const res = await client.post("/workflows", input);
  return res.data;
}

export async function updateWorkflow(
  id: string,
  input: WorkflowUpdateInput,
): Promise<Workflow> {
  const res = await client.put(`/workflows/${id}`, input);
  return res.data;
}

export async function deleteWorkflow(id: string): Promise<void> {
  await client.delete(`/workflows/${id}`);
}

export async function evaluateWorkflow(body: {
  entity_type: EntityType;
  context?: Record<string, unknown>;
  subject_employee_id?: string | null;
}): Promise<EvaluationResult> {
  const res = await client.post("/workflows/evaluate", body);
  return res.data;
}

export async function seedDefaultWorkflows(): Promise<Workflow[]> {
  const res = await client.post("/workflows/seed-defaults");
  return res.data;
}

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  leave_request: "Leave Request",
  expense: "Expense",
  job_requisition: "Job Requisition",
  document_change: "Document Change",
  compensation_change: "Compensation Change",
};

export const APPROVER_TYPE_LABELS: Record<ApproverType, string> = {
  direct_manager: "Direct Manager",
  skip_level_manager: "Skip-Level Manager",
  department_head: "Department Head",
  hr_admin: "HR Admin",
  specific_user: "Specific User",
  role: "Role",
};
